<ds-modal class="modal-container" [modal]="modal" [title]="cardTitle | translate" [closeAllModals]="false" #modalRef>
  <form [formGroup]="form" class="d-flex flex-column">
    <ds-tabs [(activeId)]="activeTabId" [tablistLabelledBy]="modalRef.titleId">
      <ds-tab [heading]="'task_details_label' | translate" [label]="'task_details_label' | translate" [headingLevel]="3">
        <div class="mb-3">{{ 'form-field-marked-as-required' | translate }}</div>
        @if (needToSelectProjectFromWorkspace) {
          <div class="d-flex gap-2 mb-4">
            @if (availableProjects$ | async; as availableProjects) {
              <div class="flex-grow-1">
                <ds-input
                  [label]="'available_projects' | translate"
                  [required]="true"
                  [instructions]="'available_projects_instructions' | translate"
                  [errorMessage]="projectInputError(availableProjects)"
                  [errorState]="formFieldErrors[$task.projectId]?.length > 0"
                >
                  <select
                    class="w-100"
                    ds-input-field
                    data-test-id="project-selector"
                    id="project-selector"
                    [formControlName]="$task.projectId"
                    (ngModelChange)="onProjectChange($event)"
                  >
                    <option [ngValue]="null" disabled>{{ projectPlaceholder(availableProjects) }}</option>
                    @for (project of availableProjects; track project[$project._id]) {
                      <option [ngValue]="project">
                        {{ project[$project.name] | ellipsis: 30 }}
                      </option>
                    }
                  </select>
                </ds-input>
                @if (userCanPushIssueIntegration$ | async) {
                  <div id="issueTracking">
                    <div class="mt-2">
                      <ds-checkbox
                        [label]="issueIntegrationProjectLabel$ | async"
                        [disabled]="(userCanPushIssueIntegration$ | async) === false"
                        [isSelected]="pushTaskToIssueTracking"
                        (valueChange)="onPushTaskToIssueTrackingChange($event)"
                      ></ds-checkbox>
                    </div>
                    @if (pushTaskToIssueTracking && issueTrackingIssueTypes$ | async; as issueTrackingIssueTypes) {
                      <div>
                        <ds-input
                          [label]="'push_task_as' | translate"
                          [required]="pushTaskToIssueTracking && issueTypeRequired"
                          [errorMessage]="formFieldErrors[$task.issueIntegrationType]"
                          [errorState]="formFieldErrors[$task.issueIntegrationType]?.length > 0"
                          class="mb-4"
                        >
                          <select
                            class="w-100"
                            ds-input-field
                            id="jira-ticket-type-selector"
                            [formControlName]="$task.issueIntegrationType"
                          >
                            <option [ngValue]="null" disabled selected>{{ selectIssueTypeLabel$ | async }}</option>
                            @if (!issueTypeRequired) {
                              <option [ngValue]="noIssueTypeId">{{ 'no_issue_type' | translate }}</option>
                            }
                            @for (issueTrackingIssueType of issueTrackingIssueTypes; track issueTrackingIssueType.id) {
                              <option [ngValue]="issueTrackingIssueType.id">
                                {{ issueTrackingIssueType.name | translate }}
                              </option>
                            }
                          </select>
                        </ds-input>
                      </div>
                    } @else {
                      @if (pushTaskToIssueTracking && loadingError$ | async) {
                        <div class="alert alert-danger" role="alert">
                          {{ 'error_loading_jira_project_issue_types' | translate }}
                        </div>
                      }
                    }
                    @if (form.contains($task.githubProjectId) && (githubProjectsV2$ | async); as githubProjectsV2) {
                      @if (githubProjectsV2.length > 0) {
                        <div>
                          <ds-input [label]="'github_project_v2' | translate" class="mb-4">
                            <select
                              class="w-100"
                              ds-input-field
                              id="github-project-v2-selector"
                              [formControlName]="$task.githubProjectId"
                            >
                              <option [ngValue]="null">{{ 'select_an_option' | translate }}</option>
                              @for (githubProjectV2 of githubProjectsV2; track githubProjectV2.id) {
                                <option [ngValue]="githubProjectV2.id">
                                  {{ githubProjectV2.title }}
                                </option>
                              }
                            </select>
                          </ds-input>
                        </div>
                      }
                    }
                  </div>
                }
              </div>
            }
            <div class="mt-5">
              <button
                ds-button
                class="create-project text-nowrap"
                type="button"
                (click)="createNewProject()"
                [variant]="DsButtonVariants.secondary"
              >
                {{ 'create_project' | translate }}
              </button>
            </div>
          </div>

          @if (projectCreatedSuccessMessage$ | async; as projectCreatedSuccessMessage) {
            <ds-banner
              [state]="DsBannerState.success"
              [showTitle]="false"
              [showAction]="false"
              [showCloseButton]="true"
              (closed)="onCreateProjectSuccessBannerClosed()"
              data-test-id="project-created-message-banner"
            >
              <div ds-banner-body>
                {{ projectCreatedSuccessMessage }}
              </div>
            </ds-banner>
          }
        }

        <div class="mt-2">
          <ds-input
            [label]="'label_task_form_name' | translate"
            [errorMessage]="formFieldErrors[$task.name]"
            [errorState]="formFieldErrors[$task.name]?.length > 0"
            [required]="true"
            class="mb-4"
          >
            <input ds-input-field [formControlName]="$task.name" />
          </ds-input>
        </div>

        <ds-input
          [label]="'label_task_form_status' | translate"
          [errorMessage]="formFieldErrors[$task.status]"
          [errorState]="formFieldErrors[$task.status]?.length > 0"
          [required]="false"
          class="mb-4"
          [state]="DsInputStates.inactive"
        >
          <input ds-input-field [id]="$task.status" [formControlName]="$task.status" />
        </ds-input>

        <ds-input [label]="'label_task_priority' | translate" [required]="false" class="mb-4">
          <select [id]="$task.priority" class="w-100" ds-input-field [formControlName]="$task.priority">
            @for (option of taskPriorityOptions; track $index) {
              <option [value]="option.priorityValue">
                {{ option.name | translate }}
              </option>
            }
          </select>
        </ds-input>

        @if (selectedIssues && selectedIssues.length > 0) {
          <label class="label mb-4 text-nowrap linked-findings-label" id="linkedFindings">
            {{ 'linked_findings' | translate }}
          </label>
          @if (!canUnlinkTask) {
            <app-alert
              [type]="alertType.danger"
              [message]="unlinkTaskErrorMessage"
              (onClose)="canUnlinkTask = true"
              class="mb-2 unlink-task-error-message"
            ></app-alert>
          }
          <app-ngb-table
            [config]="linkedFindingsTableConfig"
            [data]="linkedFindingsTableRows"
            class="linked-findings-table"
            data-e2e-id="linked-findings-table"
          >
          </app-ngb-table>
        }

        <div class="attachments-container mt-4">
          <app-file-drag-and-drop
            [form]="form"
            [formValidationRequest$]="formValidationRequest"
            (fileChange)="onFileUpload($event)"
            (existingUploadsChange)="onExistingAttachmentsChange($event)"
            [(existingUploads)]="attachments"
            [context]="uploaderContext"
            [removableFiles]="true"
            [disabled]="disableUploadButtons$ | async"
            [showBannerInsteadOfToast]="true"
          >
            @if (showAzureUploadWarning$ | async) {
              <div class="uploader-warning">
                {{ 'azure_filesize_warning' | translate: azureFileSizeLimit }}
              </div>
            }
          </app-file-drag-and-drop>
        </div>
      </ds-tab>
      <ds-tab
        [heading]="'table_header_rule_description' | translate"
        [label]="'table_header_rule_description' | translate"
        [headingLevel]="3"
      >
        <div class="margin-bottom">
          <app-markdown-editor [formControlName]="$task.description"> </app-markdown-editor>
          <app-form-field-error
            label="label_description"
            [formValidationRequest$]="formValidationRequest$"
            [field]="form.get($task.description)"
            [name]="$task.description"
          ></app-form-field-error>
        </div>
      </ds-tab>
    </ds-tabs>
  </form>
  <ng-template ds-modal-slide-out-footer>
    <ds-button-group
      [primaryAction]="{ label: submitButton | translate }"
      [secondAction]="{ label: 'form_cancel' | translate, variant: DsButtonVariants.secondary }"
      (primaryActionClicked)="submitForm()"
      (secondActionClicked)="modalService.closeCurrentModal()"
    >
    </ds-button-group>
  </ng-template>
</ds-modal>
